import { ArrowRight } from "lucide-react";
import { FanMark } from "../components/FanMark";

const RED = "#E82529";

// 3 city photos, chosen and cropped by Fede in the photo picker (Pexels
// licence, no attribution required). Each exists at 700 / 1000 / 1400 px
// wide in WebP + JPEG under /photos — the column is ~1/6 of the viewport,
// so 1400 px covers a 2× retina screen up to ~4200 px wide.
// `pos` is the focal point set in the picker (CSS object-position).
const CITIES = [
  { base: "/photos/landing-tokyo", city: "TOKYO", pos: "51% 54%" },
  { base: "/photos/landing-kyoto", city: "KYOTO", pos: "49% 53%" },
  { base: "/photos/landing-osaka", city: "OSAKA", pos: "50% 52%" },
];

// The three columns share the viewport's right half, so each is ~17vw.
const COLUMN_SIZES = "(min-width: 768px) 17vw, 33vw";

function CityPanel({ base, city, pos, index }) {
  return (
    <div
      className="relative overflow-hidden h-full"
      style={{ borderLeft: index > 0 ? "1px solid rgba(255,255,255,0.10)" : "none" }}
    >
      {/* Photo — full colour, no desaturation: the picture carries the impact. */}
      <picture>
        <source
          type="image/webp"
          sizes={COLUMN_SIZES}
          srcSet={`${base}-700.webp 700w, ${base}-1000.webp 1000w, ${base}-1400.webp 1400w`}
        />
        <img
          src={`${base}-1000.jpg`}
          sizes={COLUMN_SIZES}
          srcSet={`${base}-700.jpg 700w, ${base}-1000.jpg 1000w, ${base}-1400.jpg 1400w`}
          alt=""
          loading={index === 0 ? "eager" : "lazy"}
          fetchPriority={index === 0 ? "high" : "auto"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: pos || "center center",
            filter: "contrast(1.02)",
          }}
        />
      </picture>
      {/* Fog: bottom only, so the city label reads while the upper half
          stays full colour. (Fede's rule: legibility below, beauty above.) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 44%, rgba(0,0,0,0.42) 68%, rgba(0,0,0,0.92) 100%)",
        }}
      />
      {/* Index */}
      <div
        className="font-mono-label"
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          fontSize: 9,
          letterSpacing: "0.2em",
          color: "rgba(255,255,255,0.42)",
        }}
      >
        0{index + 1}/03
      </div>
      {/* City label */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "0 18px 18px" }}>
        <div
          style={{
            fontFamily: '"Unbounded", sans-serif',
            fontWeight: 900,
            fontSize: "clamp(15px, 2.2vw, 22px)",
            letterSpacing: "0.04em",
            color: "#F5F5F0",
            lineHeight: 1,
            textTransform: "uppercase",
            textShadow: "0 2px 12px rgba(0,0,0,0.5)",
          }}
        >
          {city}
        </div>
      </div>
    </div>
  );
}

const LandingPage = ({ onStart, userEmail, onMyTrips, onPricing, resumeTitle = "", onResume = () => {} }) => {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--zen-bg)" }}>
      <div className="flex-1 flex flex-col-reverse md:grid md:grid-cols-2 min-h-0">

        {/* ── LEFT: brand panel (desktop left / mobile bottom) ── */}
        <div
          className="flex-1 md:flex-none flex flex-col justify-between px-5 py-5 md:px-11 md:py-7 md:border-r"
          style={{ borderRightColor: "var(--zen-border)" }}
        >
          {/* Nav */}
          <div className="flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <FanMark size={22} />
              <div className="flex flex-col" style={{ lineHeight: 0.9 }}>
                <span style={{ fontFamily: '"Unbounded", sans-serif', fontWeight: 900, fontSize: 10, letterSpacing: "0.04em", color: "var(--zen-text)", textTransform: "uppercase" }}>JAPAN UN·</span>
                <span style={{ fontFamily: '"Unbounded", sans-serif', fontWeight: 900, fontSize: 10, letterSpacing: "0.04em", color: RED, textTransform: "uppercase" }}>FOLDED</span>
              </div>
            </div>
            <button
              onClick={onPricing}
              className="font-mono-label hover:opacity-70 transition-opacity"
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: 9, letterSpacing: "0.18em", color: "var(--zen-dim)" }}
              data-testid="pricing-landing-btn"
            >
              Pricing
            </button>
          </div>

          {/* Hero headline */}
          <div className="flex-1 flex flex-col justify-center" style={{ padding: "28px 0" }}>
            <div style={{ fontFamily: '"Unbounded", sans-serif', fontWeight: 900, fontSize: "clamp(2.4rem, 6.2vw, 3.9rem)", lineHeight: 0.84, letterSpacing: "0.01em", color: "var(--zen-text)", textTransform: "uppercase" }}>JAPAN</div>
            <div style={{ fontFamily: '"Unbounded", sans-serif', fontWeight: 900, fontSize: "clamp(2.4rem, 6.2vw, 3.9rem)", lineHeight: 0.84, letterSpacing: "0.01em", color: "var(--zen-text)", textTransform: "uppercase" }}>UN·<span style={{ color: RED }}>FOLDED</span></div>
            <p
              className="font-curator"
              style={{ marginTop: 20, fontSize: "clamp(14px, 1.5vw, 18px)", lineHeight: 1.6, color: "var(--zen-sub)", maxWidth: 400 }}
            >
              Transform your scattered wishlist into a perfectly organized, neighborhood-clustered itinerary. Less trains, more memories.
            </p>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-start flex-shrink-0">
            {/* the trip they left half-made in this browser (utils/tripDraft): pick it up where they stopped */}
            {resumeTitle && (
              <button onClick={onResume} data-testid="resume-trip-btn" className="hover:opacity-80 transition-opacity"
                style={{ marginBottom: 14, padding: "10px 14px", background: "var(--zen-s1)", border: "1px solid var(--zen-border)", borderRadius: 4,
                         cursor: "pointer", textAlign: "left", color: "var(--zen-text)", fontFamily: '"DM Sans", system-ui, sans-serif', fontSize: 14 }}>
                <span className="font-mono-label" style={{ display: "block", fontSize: 9, letterSpacing: "0.18em", color: "var(--zen-dim)", marginBottom: 2 }}>CONTINUE WHERE YOU LEFT OFF</span>
                <strong>{resumeTitle}</strong> <ArrowRight className="w-3.5 h-3.5" style={{ display: "inline", verticalAlign: "-2px", color: RED }} />
              </button>
            )}
            <button
              onClick={onStart}
              className="hover:opacity-90 transition-opacity"
              style={{
                padding: "16px 36px", background: RED, border: "none", color: "#fff",
                fontFamily: '"DM Sans", system-ui, sans-serif', fontWeight: 800, fontSize: 13,
                letterSpacing: "0.04em", cursor: "pointer", borderRadius: 2,
                display: "inline-flex", alignItems: "center", gap: 10, whiteSpace: "nowrap",
              }}
              data-testid="start-planning-btn"
            >
              Unfold My Plan <ArrowRight className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-5" style={{ marginTop: 18 }}>
              <button
                onClick={onPricing}
                className="font-mono-label hover:opacity-70 transition-opacity"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, letterSpacing: "0.18em", color: "var(--zen-dim)", padding: 0 }}
              >
                See pricing
              </button>
              {userEmail && (
                <button
                  onClick={onMyTrips}
                  className="font-mono-label hover:opacity-70 transition-opacity"
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, letterSpacing: "0.18em", color: "var(--zen-dim)", padding: 0 }}
                  data-testid="my-trips-landing-btn"
                >
                  My Trips
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: triptych (desktop right / mobile top) ── */}
        <div className="grid grid-cols-3 overflow-hidden h-[42vh] md:h-auto" style={{ minHeight: 0 }}>
          {CITIES.map((c, i) => (
            <CityPanel key={c.city} base={c.base} city={c.city} pos={c.pos} index={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
